
import { useState } from "react";
import { AdminContextType } from "./types";
import { useAdminState } from "@/hooks/admin/useAdminState";
import { useUserRole } from "@/hooks/admin/useUserRole";
import { useAdminDataLoader } from "@/hooks/admin/useAdminDataLoader";
import { useAdminUserManagement } from "@/hooks/admin/useAdminUserManagement";
import { useStrategyProviderManagement } from "@/hooks/admin/useStrategyProviderManagement";
import { useAccountsManagement } from "@/hooks/admin/useAccountsManagement";
import { useContentManagement } from "@/hooks/admin/useContentManagement";
import { usePerformanceData } from "@/hooks/admin/usePerformanceData";

export const useAdminContextValue = (): AdminContextType => {
  // Admin state and user role
  const {
    activeTab,
    setActiveTab,
    accountPerformances,
    setAccountPerformances,
    loadingStripe,
    setLoadingStripe,
    userRole,
    setUserRole,
    providerId,
    setProviderId,
    stripeSubscriptions,
    setStripeSubscriptions
  } = useAdminState();
  
  // User role management
  useUserRole(setUserRole, setProviderId);
  
  // User management hooks
  const { 
    users, 
    usersLoading,
    loadUsers, 
    updateUserRole,
    updateUserName,
    deleteUser,
    sortField,
    sortOrder,
    handleSort,
    getUsersForProviders
  } = useAdminUserManagement();

  // Strategy and provider management hooks
  const {
    strategies,
    strategiesLoading,
    loadStrategies,
    createStrategy,
    updateStrategy,
    deleteStrategy,
    toggleStrategyStatus,
    toggleStrategyActive,
    approveStrategy,
    providers,
    providersLoading,
    loadProviders,
    createProvider,
    updateProvider,
    deleteProvider,
    toggleProviderActive,
    assignUserToProvider,
    removeUserFromProvider
  } = useStrategyProviderManagement(providerId);

  // Accounts management hooks
  const {
    leadAccounts,
    leadAccountsLoading,
    loadLeadAccounts,
    createLeadAccount,
    updateLeadAccount,
    deleteLeadAccount,
    accounts,
    accountsLoading,
    loadAccounts,
    createAccount,
    updateAccount,
    deleteAccount,
    toggleAccountActive
  } = useAccountsManagement(providerId);

  // Performance and subscription data hooks
  const {
    fetchStripeSubscriptions,
    fetchAccountPerformances
  } = usePerformanceData(userRole, providerId, setLoadingStripe, setStripeSubscriptions);

  // Content management hooks (articles, faqs)
  const {
    articles,
    articleTags,
    articleAuthors,
    articlesLoading,
    faqs,
    faqsLoading,
    loadArticles,
    loadArticleTags,
    loadArticleAuthors,
    createArticle,
    updateArticle,
    deleteArticle,
    createArticleTag,
    uploadArticleMedia,
    fetchArticleById,
    fetchMedia,
    updateMediaMetadata,
    deleteMedia,
    createAuthor,
    updateAuthor,
    deleteAuthor,
    loadFaqs,
    createFaq,
    updateFaq,
    deleteFaq
  } = useContentManagement(userRole);
  
  // Load admin data based on role
  useAdminDataLoader({
    userRole,
    providerId,
    loadUsers,
    loadStrategies,
    loadProviders,
    loadLeadAccounts,
    loadAccounts,
    fetchAccountPerformances,
    fetchStripeSubscriptions,
    setAccountPerformances,
    setStripeSubscriptions
  });

  // Combined loading state
  const loading = usersLoading || strategiesLoading || providersLoading || 
                  leadAccountsLoading || accountsLoading || loadingStripe || 
                  articlesLoading || faqsLoading;

  // Combined context value
  return {
    activeTab,
    setActiveTab,
    users,
    strategies,
    providers,
    leadAccounts,
    accounts,
    accountPerformances,
    stripeSubscriptions,
    loading,
    getUsersForProviders,
    updateUserRole,
    updateUserName,
    deleteUser,
    toggleStrategyStatus,
    createStrategy,
    updateStrategy,
    deleteStrategy,
    toggleStrategyActive,
    approveStrategy,
    createProvider,
    updateProvider,
    deleteProvider,
    toggleProviderActive,
    assignUserToProvider,
    removeUserFromProvider,
    createLeadAccount,
    updateLeadAccount,
    deleteLeadAccount,
    createAccount,
    updateAccount,
    deleteAccount,
    toggleAccountActive,
    sortField,
    sortOrder,
    handleSort,
    userRole,
    
    // Articles related
    articles,
    articleTags,
    articleAuthors,
    loadArticles,
    loadArticleTags,
    loadArticleAuthors,
    createArticle,
    updateArticle,
    deleteArticle,
    createArticleTag,
    uploadArticleMedia,
    fetchArticleById,
    fetchMedia,
    updateMediaMetadata,
    deleteMedia,
    
    // FAQs related
    faqs,
    loadFaqs,
    createFaq,
    updateFaq,
    deleteFaq,
    
    // Authors related
    createAuthor,
    updateAuthor,
    deleteAuthor
  };
};
